import { useContext, useEffect, useState } from "react";
import {
  BreadTransaction,
  P,
  Predicate,
  FirestoreDocCategory,
  FirestoreDocCategoryGroup,
} from "breadcommon";
import { TransactionColumn } from "./TransactionColumn";
import { TransactionsNavContext } from "./TransactionsScreen";
import { buildClasses } from "../utils/buildClasses";
import { RyeDropDownMultiSelect } from "../rye/RyeDropDownMultiSelect";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { CategoryGroupsContext } from "../firebaseio/CategoryGroupsContext";
import { TransactionColumnFilterProps } from "./DefaultTransactionColumnHeader";

export function TransactionColumnFilterCategory({
  isOpen,
  onClose,
  hasActiveFilter,
}: TransactionColumnFilterProps): JSX.Element | null {
  const [transactionsNav, setTransactionsNav] = useContext(
    TransactionsNavContext,
  );
  const categories = useContext(CategoriesContext);
  const categoryGroups = useContext(CategoryGroupsContext);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  useEffect(() => {
    if (!hasActiveFilter) {
      setSelectedCategoryIds([]);
    }
  }, [hasActiveFilter]);

  function createCategoryPredicate(categoryIds: string[]): Predicate | null {
    if (categoryIds.length === 0) {
      return null;
    }

    const field = P.Field<BreadTransaction, "categoryId">("categoryId");
    const predicates = categoryIds.map((id) => P.Equals(field, P.Literal(id)));
    return P.Any(...predicates);
  }

  function applyFilter(categoryIds: string[]) {
    const currentSettings =
      transactionsNav.columnSettings[TransactionColumn.CATEGORY];
    setTransactionsNav((prevState) => ({
      ...prevState,
      columnSettings: {
        ...transactionsNav.columnSettings,
        [TransactionColumn.CATEGORY]: {
          ...currentSettings,
          predicate: createCategoryPredicate(categoryIds),
        },
      },
    }));
  }

  function handleSelectionChange(categoryIds: string[]) {
    setSelectedCategoryIds(categoryIds);
    applyFilter(categoryIds);
  }

  if (!isOpen) {
    return null;
  }

  // Prepare category entries with grouping
  const categoryEntries = Array.from(categories.values())
    .sort(
      (a: FirestoreDocCategory, b: FirestoreDocCategory) =>
        a.order_position - b.order_position,
    )
    .map((category: FirestoreDocCategory) => ({
      id: category.id,
      text: category.name,
      icon: category.emoji,
      groupId: category.category_group_id,
    }));

  const groups = Array.from(categoryGroups.values())
    .sort(
      (a: FirestoreDocCategoryGroup, b: FirestoreDocCategoryGroup) =>
        a.order_position - b.order_position,
    )
    .map((group: FirestoreDocCategoryGroup) => ({
      id: group.id,
      label: group.name,
    }));

  return (
    <div
      className={buildClasses("flex", "flex-col", "gap-2")}
      tabIndex={-1}
    >
      <RyeDropDownMultiSelect
        selectedEntryIds={selectedCategoryIds}
        entries={categoryEntries}
        groups={groups}
        onSelectionChange={handleSelectionChange}
        onEsc={onClose}
        searchable
        size="md"
        width="lg"
        variant="outlined"
      />
    </div>
  );
}
